import { useEffect, useRef, useState } from 'react';
import { getApiBaseUrl } from '../api/apiClient';

/**
 * ``https:`` thumbnails load directly in ``<img>`` (CSP). Other URLs (``http:``, API-relative paths)
 * are fetched and shown as a ``blob:`` URL so ``img-src`` does not need host-specific rules.
 */
export function useSampleTemplateThumbnailSrc(thumbnail) {
  const [displaySrc, setDisplaySrc] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    setLoadFailed(false);
    const raw = typeof thumbnail === 'string' ? thumbnail.trim() : '';

    const revokeBlob = () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };

    if (!raw) {
      revokeBlob();
      setDisplaySrc('');
      return undefined;
    }

    if (raw.startsWith('https:')) {
      revokeBlob();
      setDisplaySrc(raw);
      return undefined;
    }

    const fetchUrl = raw.startsWith('/')
      ? `${getApiBaseUrl().replace(/\/$/, '')}${raw}`
      : raw;

    revokeBlob();
    setDisplaySrc('');

    let cancelled = false;
    const ac = new AbortController();
    fetch(fetchUrl, { signal: ac.signal, credentials: 'omit' })
      .then((r) => {
        if (!r.ok) throw new Error('thumb');
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const u = URL.createObjectURL(blob);
        blobUrlRef.current = u;
        setDisplaySrc(u);
      })
      .catch(() => {
        if (!cancelled) setLoadFailed(true);
      });

    return () => {
      cancelled = true;
      ac.abort();
      revokeBlob();
      setDisplaySrc('');
    };
  }, [thumbnail]);

  return { displaySrc, loadFailed, setLoadFailedFromImg: () => setLoadFailed(true) };
}
